import React, { useState } from 'react';
import styled from 'styled-components';
import { useLanguage } from '../contexts/LanguageContext';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
import Footer from '../components/shared/Footer';
import LanguageToggle from '../components/common/LanguageToggle';
import { glassTheme } from '../theme/glassStyles';

const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  width: 100%;
  position: relative;

  &::before {
    content: '';
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: url(${glassTheme.backgrounds.page.image});
    background-size: cover;
    background-position: center;
    z-index: -1;
  }
`;

const MainContent = styled.div`
  width: 100%;
`;

const Section = styled.section`
  color: white;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const SectionContent = styled.div`
  max-width: 1200px;
  width: 100%;
  padding: 60px 24px;
  position: relative;
  z-index: 2;
`;

const MentorshipSection = styled(Section)`
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${glassTheme.backgrounds.sections.mentor.background};
    backdrop-filter: blur(${glassTheme.backgrounds.sections.mentor.blur});
    border: 1px solid ${glassTheme.backgrounds.sections.mentor.border};
    z-index: 1;
  }
`;

const MentorshipOptions = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 32px;
  margin: 48px 0;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 24px;
    margin: 32px 0;
  }
`;

const OptionCard = styled(motion.div)`
  background: ${glassTheme.backgrounds.cards.option.background};
  backdrop-filter: blur(${glassTheme.backgrounds.cards.option.blur});
  opacity: ${glassTheme.backgrounds.cards.option.opacity};
  border: 1px solid ${glassTheme.backgrounds.cards.option.border};
  border-radius: 24px;
  padding: 40px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  position: relative;
  overflow: visible;

  @media (max-width: 768px) {
    padding: 24px;
  }

  h3 {
    font-size: 24px;
    margin-bottom: 8px;
    color: #00C6FF;
    display: flex;
    align-items: center;
    gap: 12px;

    @media (max-width: 768px) {
      font-size: 20px;
    }
  }

  h4 {
    font-size: 18px;
    color: rgba(255, 255, 255, 0.9);
    margin-bottom: 24px;
    font-style: italic;

    @media (max-width: 768px) {
      font-size: 16px;
      margin-bottom: 16px;
    }
  }

  p {
    font-size: 16px;
    line-height: 1.6;
    color: rgba(255, 255, 255, 0.8);
    margin-bottom: 24px;

    @media (max-width: 768px) {
      font-size: 14px;
      margin-bottom: 16px;
    }
  }

  ul {
    list-style: none;
    padding: 0;
    margin: 0;

    li {
      font-size: 15px;
      color: rgba(255, 255, 255, 0.7);
      margin-bottom: 12px;
      display: flex;
      align-items: flex-start;
      gap: 8px;

      @media (max-width: 768px) {
        font-size: 14px;
        margin-bottom: 8px;
      }

      &:before {
        content: '•';
        color: #00C6FF;
      }
    }
  }
`;

const SectionTitle = styled.h2`
  font-size: 48px;
  text-align: center;
  margin-bottom: 24px;
  background: linear-gradient(135deg, #FFFFFF 0%, #00C6FF 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-weight: 700;

  @media (max-width: 768px) {
    font-size: 32px;
    margin-bottom: 16px;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 20px;
  text-align: center;
  color: rgba(255, 255, 255, 0.8);
  max-width: 800px;
  margin: 0 auto 48px;
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 16px;
    margin: 0 auto 32px;
    padding: 0 16px;
  }
`;

const LanguageContainer = styled.div`
  position: absolute;
  top: 24px;
  right: 24px;
  z-index: 10;
`;

const RevenueSection = styled(Section)`
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${glassTheme.backgrounds.sections.revenue.background};
    backdrop-filter: blur(${glassTheme.backgrounds.sections.revenue.blur});
    border: 1px solid ${glassTheme.backgrounds.sections.revenue.border};
    z-index: 1;
  }

  &::after {
    display: none;
  }
`;

const RevenueTitle = styled.h2`
  font-size: 48px;
  text-align: center;
  margin-bottom: 40px;
  background: ${glassTheme.backgrounds.titles.gradient};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-weight: 600;
`;

const RevenueCalculator = styled.div`
  background: ${glassTheme.backgrounds.cards.calculator.background};
  backdrop-filter: blur(${glassTheme.backgrounds.cards.calculator.blur});
  border: 1px solid ${glassTheme.backgrounds.cards.calculator.border};
  border-radius: 24px;
  padding: 48px;
  max-width: 700px;
  margin: 0 auto;
  text-align: center;
  position: relative;
  box-shadow: ${glassTheme.backgrounds.cards.calculator.shadow};

  @media (max-width: 768px) {
    padding: 24px;
    margin: 0 16px;
  }
`;

const CalculatorGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 32px;
  margin: 32px 0;
  position: relative;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 24px;
    margin: 24px 0;

    &::after {
      display: none;
    }
  }

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 1px;
    height: 80%;
    background: rgba(255, 255, 255, 0.1);
  }
`;

const LabelText = styled.span`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 1px;
  white-space: pre-line;
  text-align: center;
  line-height: 1.4;
`;

const CalculatorInput = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  position: relative;

  input {
    width: 180px;
    text-align: center;
    background: ${glassTheme.backgrounds.cards.input.background};
    border: 1px solid ${glassTheme.backgrounds.cards.input.border};
    border-radius: 12px;
    padding: 16px;
    color: white;
    font-size: 28px;
    font-weight: 600;
    transition: all 0.3s ease;

    @media (max-width: 768px) {
      width: 140px;
      font-size: 24px;
      padding: 12px;
    }

    &:focus {
      outline: none;
      border-color: ${glassTheme.backgrounds.cards.input.focusBorder};
      background: ${glassTheme.backgrounds.cards.input.focusBackground};
      box-shadow: ${glassTheme.backgrounds.cards.input.shadow};
    }

    &::placeholder {
      color: rgba(255, 255, 255, 0.3);
    }
  }
`;

const RevenueResult = styled(motion.div)`
  margin-top: 40px;
  padding: 32px;
  background: ${glassTheme.backgrounds.cards.result.background};
  border-radius: 16px;
  border: 1px solid ${glassTheme.backgrounds.cards.result.border};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  position: relative;

  h4 {
    font-size: 18px;
    color: #00C6FF;
    margin-bottom: 8px;
    text-transform: uppercase;
    letter-spacing: 1px;
    font-weight: 500;
  }

  p {
    font-size: 48px;
    background: linear-gradient(135deg, #FFFFFF 0%, #00C6FF 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    font-weight: 700;
    margin: 0;
  }

  span {
    font-size: 16px;
    color: rgba(255, 255, 255, 0.6);
    font-weight: 500;
  }
`;

const FormSection = styled(Section)`
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(10px);
    z-index: 1;
  }

  ${SectionTitle} {
    margin-bottom: 48px;
  }
`;

const FormContainer = styled.form`
  background: ${glassTheme.backgrounds.cards.form.background};
  backdrop-filter: blur(${glassTheme.backgrounds.cards.form.blur});
  border: 1px solid ${glassTheme.backgrounds.cards.form.border};
  border-radius: 24px;
  padding: 48px;
  max-width: 800px;
  margin: 0 auto;
  width: 100%;
  position: relative;
  z-index: 2;
  box-shadow: ${glassTheme.backgrounds.cards.form.shadow};
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 32px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    padding: 32px;
  }
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;

  &:nth-child(3),
  &:nth-child(4) {
    grid-column: 1 / -1;
  }
`;

const Label = styled.label`
  font-size: 16px;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 500;
`;

const Input = styled.input`
  background: rgba(255, 255, 255, 0.07);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 16px;
  color: white;
  font-size: 16px;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: #00C6FF;
    background: rgba(255, 255, 255, 0.1);
    box-shadow: 0 0 0 2px rgba(0, 198, 255, 0.2);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

const TextArea = styled.textarea`
  background: rgba(255, 255, 255, 0.07);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 16px;
  color: white;
  font-size: 16px;
  min-height: 160px;
  resize: vertical;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: #00C6FF;
    background: rgba(255, 255, 255, 0.1);
    box-shadow: 0 0 0 2px rgba(0, 198, 255, 0.2);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

const SubmitButton = styled.button`
  background: linear-gradient(135deg, #00C6FF 0%, #0072FF 100%);
  border: none;
  border-radius: 12px;
  padding: 18px 40px;
  color: white;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  grid-column: 1 / -1;
  justify-self: center;
  width: auto;
  min-width: 250px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 16px rgba(0, 198, 255, 0.2);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    &:hover {
      transform: none;
      box-shadow: none;
    }
  }
`;

const SuccessContainer = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 40px;
`;

const CheckmarkCircle = styled(motion.div)`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, #00C6FF 0%, #0072FF 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
`;

const SuccessTitle = styled.h3`
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 16px;
  color: white;
`;

const SuccessMessage = styled.p`
  font-size: 18px;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.8);
  max-width: 400px;
`;

const CommissionLabel = styled.span`
  position: absolute;
  top: -12px;
  right: -12px;
  padding: 6px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: ${props => glassTheme.backgrounds.labels[props.type].background};
  color: ${props => glassTheme.backgrounds.labels[props.type].color};
  border: 1px solid ${props => glassTheme.backgrounds.labels[props.type].border};
  backdrop-filter: blur(${props => glassTheme.backgrounds.labels[props.type].blur});
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 3;
  transform: rotate(5deg);
  transition: transform 0.3s ease;

  &:hover {
    transform: rotate(0deg) scale(1.05);
  }
`;

const MentorFooter = styled.footer`
  width: 100%;
  padding: 24px 40px;
  background: ${glassTheme.backgrounds.footer.background};
  border-top: 1px solid ${glassTheme.backgrounds.footer.border};
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 600px) {
    flex-direction: column-reverse;
    gap: 16px;
    padding: 16px;
    text-align: center;
  }
`;

const FooterText = styled.div`
  color: rgba(255, 255, 255, 0.6);
  font-size: 14px;

  @media (max-width: 600px) {
    font-size: 12px;
  }
`;

const BackLink = styled.a`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 12px 24px;
  color: white;
  text-decoration: none;
  font-size: 14px;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.15);
    border-color: rgba(255, 255, 255, 0.3);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 600px) {
    font-size: 13px;
    padding: 10px 20px;
  }
`;

const ErrorMessage = styled.div`
  color: red;
  font-size: 14px;
  margin-top: 16px;
`;

const Mentor = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    expertise: '',
    motivation: ''
  });
  const [calculatorData, setCalculatorData] = useState({
    companyCount: '100',
    referralPercentage: '50'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const calculateRevenue = (count, percentage) => {
    const countNum = parseInt(count) || 0;
    const percentageNum = parseInt(percentage) || 0;
    const referredCompanies = Math.floor(countNum * (percentageNum / 100));
    const nonReferredCompanies = countNum - referredCompanies;
    
    const referredRevenue = referredCompanies * 15000;
    const nonReferredRevenue = nonReferredCompanies * 5000;
    const totalAnnualRevenue = referredRevenue + nonReferredRevenue;
    const monthlyRevenue = totalAnnualRevenue / 12;

    // Format numbers with k suffix
    const formatNumber = (num) => {
      if (num >= 1000000) {
        return `${Math.round(num / 100000) / 10}M`;
      }
      if (num >= 1000) {
        return `${Math.round(num / 100) / 10}k`;
      }
      return Math.round(num).toString();
    };

    return {
      monthly: formatNumber(monthlyRevenue),
      annual: formatNumber(totalAnnualRevenue)
    };
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCalculatorChange = (e) => {
    const { name, value } = e.target;
    // Only allow numbers and empty string
    if (value === '' || /^\d+$/.test(value)) {
      setCalculatorData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    const message = `
Q: Area of Expertise
A: ${formData.expertise}

Q: Why do you want to be a mentor?
A: ${formData.motivation}
    `.trim();

    try {
      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          message: message
        })
      });

      if (response.ok) {
        setSubmitStatus('success');
      } else {
        const errorData = await response.json();
        console.error("Error sending mentor application:", errorData);
        setSubmitStatus({ status: 'error', message: errorData.message || 'Failed to submit application. Please try again.' });
      }
    } catch (error) {
      console.error("Network or other error during mentor application:", error);
      setSubmitStatus({ status: 'error', message: 'An unexpected error occurred. Please check your connection and try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const revenue = calculateRevenue(calculatorData.companyCount, calculatorData.referralPercentage);

  return (
    <PageContainer>
      <LanguageContainer>
        <LanguageToggle />
      </LanguageContainer>
      
      <MainContent>
        <MentorshipSection>
          <SectionContent>
            <SectionTitle>{t('mentor.title')}</SectionTitle>
            <SectionSubtitle>
              {t('mentor.subtitle')}
            </SectionSubtitle>
            
            <MentorshipOptions>
              <OptionCard
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <CommissionLabel type="quarter">1/4 Commission</CommissionLabel>
                <h3>{t('mentor.options.whisperer.title')}</h3>
                <h4>{t('mentor.options.whisperer.subtitle')}</h4>
                <p>{t('mentor.options.whisperer.description')}</p>
                <ul>
                  <li>{t('mentor.options.whisperer.features.length')}</li>
                  <li>{t('mentor.options.whisperer.features.format')}</li>
                  <li>{t('mentor.options.whisperer.features.purpose')}</li>
                </ul>
              </OptionCard>

              <OptionCard
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <CommissionLabel type="full">Full Commission</CommissionLabel>
                <h3>{t('mentor.options.guardian.title')}</h3>
                <h4>{t('mentor.options.guardian.subtitle')}</h4>
                <p>{t('mentor.options.guardian.description')}</p>
                <ul>
                  <li>{t('mentor.options.guardian.features.length')}</li>
                  <li>{t('mentor.options.guardian.features.sessions')}</li>
                  <li>{t('mentor.options.guardian.features.purpose')}</li>
                </ul>
              </OptionCard>
            </MentorshipOptions>
          </SectionContent>
        </MentorshipSection>

        <RevenueSection>
          <SectionContent>
            <RevenueCalculator>
              <RevenueTitle>{t('mentor.revenue.title')}</RevenueTitle>
              <CalculatorGrid>
                <CalculatorInput>
                  <motion.input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    name="companyCount"
                    value={calculatorData.companyCount}
                    onChange={handleCalculatorChange}
                    min="0"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                  />
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.6, delay: 0.3 }}
                  >
                    <LabelText>{t('mentor.revenue.calculator.companies')}</LabelText>
                  </motion.div>
                </CalculatorInput>
                <CalculatorInput>
                  <motion.input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    name="referralPercentage"
                    value={calculatorData.referralPercentage}
                    onChange={handleCalculatorChange}
                    min="0"
                    max="100"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                  />
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.6, delay: 0.3 }}
                  >
                    <LabelText>{t('mentor.revenue.calculator.referral')}</LabelText>
                  </motion.div>
                </CalculatorInput>
              </CalculatorGrid>
              <RevenueResult
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                <h4>{t('mentor.revenue.calculator.monthly')}</h4>
                <motion.p
                  key={revenue.monthly}
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ type: "spring", stiffness: 200, damping: 10 }}
                >
                  ${revenue.monthly} USD
                </motion.p>
                <span>≈ ${revenue.annual} USD {t('mentor.revenue.calculator.annual')}</span>
              </RevenueResult>
            </RevenueCalculator>
          </SectionContent>
        </RevenueSection>

        <FormSection>
          <SectionContent>
            <SectionTitle>{t('mentor.form.title')}</SectionTitle>
            {submitStatus === 'success' ? (
              <SuccessContainer
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <CheckmarkCircle
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                >
                  <Check size={40} />
                </CheckmarkCircle>
                <SuccessTitle>{t('mentor.form.success.title')}</SuccessTitle>
                <SuccessMessage>
                  {t('mentor.form.success.message')}
                </SuccessMessage>
              </SuccessContainer>
            ) : (
              <FormContainer onSubmit={handleSubmit}>
                <InputGroup>
                  <Label>{t('mentor.form.fields.name.label')}</Label>
                  <Input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder={t('mentor.form.fields.name.placeholder')}
                  />
                </InputGroup>
                <InputGroup>
                  <Label>{t('mentor.form.fields.email.label')}</Label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder={t('mentor.form.fields.email.placeholder')}
                  />
                </InputGroup>
                <InputGroup>
                  <Label>{t('mentor.form.fields.expertise.label')}</Label>
                  <Input
                    type="text"
                    name="expertise"
                    value={formData.expertise}
                    onChange={handleChange}
                    required
                    placeholder={t('mentor.form.fields.expertise.placeholder')}
                  />
                </InputGroup>
                <InputGroup>
                  <Label>{t('mentor.form.fields.motivation.label')}</Label>
                  <TextArea
                    name="motivation"
                    value={formData.motivation}
                    onChange={handleChange}
                    required
                    placeholder={t('mentor.form.fields.motivation.placeholder')}
                  />
                </InputGroup>
                <SubmitButton type="submit" disabled={isSubmitting}>
                  {isSubmitting ? t('mentor.form.submitting') : t('mentor.form.submit')}
                </SubmitButton>
                {submitStatus && submitStatus.status === 'error' && (
                  <ErrorMessage>{submitStatus.message || t('mentor.form.error')}</ErrorMessage>
                )}
              </FormContainer>
            )}
          </SectionContent>
        </FormSection>
      </MainContent>
      
      <MentorFooter>
        <FooterText>{t('mentor.footer.copyright')}</FooterText>
        <BackLink href="/ai-employee">
          {t('mentor.footer.back')}
        </BackLink>
      </MentorFooter>
    </PageContainer>
  );
};

export default Mentor; 